import { syntheticDeadline } from '@app/shared/workflow-deadlines/workflow-deadline-preview';
import { domainBlockManifests } from '@app/phms/workflow-blocks/domain-block-manifests';
import { domainBlockPreview } from '@app/phms/workflow-blocks/domain-block-preview';
import { caseSnapshotPreview } from '@app/phms/workflow-blocks/case-snapshot-block-preview';
import type {
  ContextReadTransport,
  WorkspaceContext,
} from '@template-ui/blocks';
import type { WorkflowCompletionChecklist } from '@app/shared/workflow-commands/workflow-completion-requirements';

/** The authoring canvas supplies these values; none are observations of runtime records. */
export interface WorkflowBlockPreview {
  processName?: string | null;
  nodeId: string | null;
  stepLabel: string | null;
  selectedOccurrenceId: number | null;
  definitionVersionId: number;
  isCurrent: boolean;
  subject: { type: string | null; id: number | null };
  execution: {
    workflowInstanceId: number;
    workflowCode: string;
    definitionVersionCode: string;
    statusCode: string;
    publicReference?: string | null;
    occurrences: readonly {
      id: number;
      stepCode: string;
      statusCode: string;
      occurrenceNumber: number;
    }[];
  };
}

/** Pure local transport for the actual registered staff panels in Edit/Try. Never performs HTTP reads. */
export function createWorkflowBlockPreviewTransport(
  preview: WorkflowBlockPreview,
  checklist: WorkflowCompletionChecklist | null,
  observedAtUtc = new Date().toISOString(),
): ContextReadTransport {
  return {
    read: async (request, context, signal) => {
      if (signal.aborted) throw new Error('Synthetic preview read cancelled.');
      const code = request.read.code;
      if (
        request.provider === 'hearing' &&
        request.read.version === 1 &&
        ['hearing.panel', 'hearing.decision', 'hearing.grant-review'].includes(
          code,
        ) &&
        request.read.dataContract.code === code + '.output' &&
        request.read.dataContract.version === 1
      ) {
        selectedPreview(preview, context, true);
        const scope = {
          workflowId: context.workflow!.workflowInstanceId,
          occurrenceId: context.workflow!.stepOccurrenceId,
        };
        const workspace =
          code === 'hearing.grant-review'
            ? {
                ...scope,
                decisionId: 1,
                hearingId: 1,
                reviewId: 1,
                status: 'pending',
                canOpen: false,
                issue: null,
              }
            : {
                ...scope,
                code,
                hearingId: 1,
                historical: false,
                canWrite: false,
                eTag: '',
                sourceHash: '',
                issue: null,
                members: [
                  {
                    officialUserId: 1,
                    officialDisplayName: 'Example presiding official',
                    isPresiding: true,
                  },
                  {
                    officialUserId: 2,
                    officialDisplayName: 'Example panel member',
                    isPresiding: false,
                  },
                ],
                candidates: [],
                recorded: null,
                recordedBy: null,
                decidedBy: null,
                evidence:
                  code === 'hearing.decision'
                    ? {
                        analysisRevision: 1,
                        hearingDate: '2026-11-18',
                        result: {
                          outcomeCode: 'approved',
                          participatingCount: 2,
                          eligibleCount: 2,
                        },
                      }
                    : null,
                sdmfSnapshot: null,
              };
        return {
          data: {
            syntheticPreview: true,
            workspaceJson: JSON.stringify(workspace),
          },
          sources: [
            {
              provider: 'hearing',
              contract: request.read.dataContract,
              recordKey: `synthetic-preview/visit:${context.workflow!.stepOccurrenceId}/${code}`,
              sourceVersion: 'synthetic-preview-v1',
              observedAtUtc,
              observation: 'client-receipt',
              consistency: 'independent-read',
            },
          ],
        };
      }
      if (
        request.provider === 'scheduling' &&
        ['scheduling.hearing', 'scheduling.history'].includes(code) &&
        request.read.version === 1 &&
        request.read.dataContract.code === code + '.output' &&
        request.read.dataContract.version === 1
      ) {
        selectedPreview(preview, context, true);
        return {
          data: {
            syntheticPreview: true,
            workspaceJson: JSON.stringify({
              workflowId: context.workflow!.workflowInstanceId,
              occurrenceId: context.workflow!.stepOccurrenceId,
              historical: false,
              requestId: 1,
              revision: 1,
              scheduledProceedingId: 1,
              hearingCycleId: 1,
              reference: 'Example hearing',
              status: 'active',
              bookingStatus: 'scheduled',
              scheduledStartUtc: '2026-11-18T17:00:00Z',
              scheduledEndUtc: '2026-11-18T18:00:00Z',
              timeZoneId: 'America/Los_Angeles',
              locationName: 'Central office',
              roomName: 'Hearing room 2',
              earliestDate: '2026-11-01',
              dueDate: '2026-11-30',
              basisIssue: null,
              canWrite: false,
              eTag: '',
              schedulingUrl: null,
              history: [],
            }),
          },
          sources: [
            {
              provider: 'scheduling',
              contract: request.read.dataContract,
              recordKey: `synthetic-preview/visit:${context.workflow!.stepOccurrenceId}/${code}`,
              sourceVersion: 'synthetic-preview-v1',
              observedAtUtc,
              observation: 'client-receipt',
              consistency: 'independent-read',
            },
          ],
        };
      }
      if (
        request.provider === 'phms' &&
        code === 'case.snapshot' &&
        request.read.version === 1 &&
        request.read.dataContract.code === 'case.snapshot.result' &&
        request.read.dataContract.version === 1
      ) {
        selectedPreview(preview, context, true);
        return {
          data: caseSnapshotPreview(context),
          sources: [
            {
              provider: 'phms',
              contract: request.read.dataContract,
              recordKey: `synthetic-preview/visit:${context.workflow!.stepOccurrenceId}/${code}`,
              sourceVersion: 'synthetic-preview-v1',
              observedAtUtc,
              observation: 'client-receipt',
              consistency: 'independent-read',
            },
          ],
        };
      }
      if (
        request.provider === 'voting' &&
        request.read.version === 1 &&
        ['voting.population', 'voting.ballots', 'voting.result'].includes(
          code,
        ) &&
        request.read.dataContract.code === code + '.output' &&
        request.read.dataContract.version === 1
      ) {
        selectedPreview(preview, context, true);
        return {
          data: { syntheticPreview: true },
          sources: [
            {
              provider: 'voting',
              contract: request.read.dataContract,
              recordKey: `synthetic-preview/visit:${context.workflow!.stepOccurrenceId}/${code}`,
              sourceVersion: 'synthetic-preview-v1',
              observedAtUtc,
              observation: 'client-receipt',
              consistency: 'independent-read',
            },
          ],
        };
      }
      const domainManifest = domainBlockManifests.find(
        (manifest) =>
          manifest.code === code &&
          manifest.provider === request.provider &&
          manifest.version === request.read.version,
      );
      if (domainManifest) {
        selectedPreview(preview, context, true);
        if (
          request.read.version !== domainManifest.reads[0].version ||
          request.read.dataContract.code !== `${code}.result` ||
          request.read.dataContract.version !==
            domainManifest.reads[0].dataContract.version
        )
          throw new Error(
            'No compatible synthetic preview is available for this block.',
          );
        return {
          data: domainBlockPreview(
            code,
            context,
            observedAtUtc,
            domainManifest.version,
          ),
          sources: [
            {
              provider: request.provider,
              contract: request.read.dataContract,
              recordKey: `synthetic-preview/visit:${context.workflow!.stepOccurrenceId}/${code}`,
              sourceVersion: 'synthetic-preview-v1',
              observedAtUtc,
              observation: 'client-receipt',
              consistency: 'independent-read',
            },
          ],
        };
      }
      const output =
        code === 'workflow.review.records'
          ? 'workflow.review.records.page'
          : code === 'workflow.step.deadlines'
            ? 'workflow.step.deadlines.result'
            : [
                  'workflow.step.information',
                  'workflow.step.requirements-evidence',
                  'workflow.step.activity',
                  'workflow.step.shared-notes',
                  'workflow.step.note-timeline',
                  'workflow.step.documents',
                ].includes(code)
              ? `${code}.page`
              : null;
      if (
        request.provider !== 'platform' ||
        request.read.version !== 1 ||
        output === null ||
        request.read.dataContract.code !== output ||
        request.read.dataContract.version !== 1
      )
        throw new Error(
          'No compatible synthetic preview is available for this block.',
        );
      const selection = selectedPreview(
        preview,
        context,
        code !== 'workflow.review.records',
      );
      let data: unknown;
      if (code === 'workflow.step.documents')
        data = {
          syntheticPreview: true,
          occurrenceId: selection.occurrenceId,
          page: 1,
          pageSize: 25,
          totalCount: 1,
          rows: [
            {
              receiptId: 1,
              occurrenceId: selection.occurrenceId,
              occurrenceNumber: 1,
              stepLabel: selection.stepLabel,
              documentLabel: 'Example supporting document',
              fileName: 'example-document.pdf',
              contentType: 'application/pdf',
              length: 24576,
              uploadedUtc: observedAtUtc,
              uploadedBy: 'Example staff member',
              canOpen: true,
              unavailableReason: null,
              scanStatus: 'clean',
            },
          ],
          isCurrent: false,
          subjectContextJson: null,
          relatedDocumentsAvailability: 'unresolved',
        };
      else if (code === 'workflow.step.deadlines')
        data = {
          syntheticPreview: true,
          occurrenceId: selection.occurrenceId,
          totalCount: 1,
          asOfUtc: observedAtUtc,
          state: 'recorded',
          rows: [
            syntheticDeadline(
              {
                workflowInstanceId: context.workflow!.workflowInstanceId,
                occurrenceId: selection.occurrenceId!,
                definitionVersionId: context.workflow!.definitionVersionId,
              },
              observedAtUtc,
            ),
          ],
        };
      else if (
        code === 'workflow.step.shared-notes' ||
        code === 'workflow.step.note-timeline'
      )
        data = {
          syntheticPreview: true,
          context: {
            version: 1,
            workflowInstanceId: context.workflow!.workflowInstanceId,
            occurrenceId: selection.occurrenceId,
            definitionVersionId: context.workflow!.definitionVersionId,
            configurationVersionId: 1,
            definitionHash: '0'.repeat(64),
            subjectTypeCode: 'hearing_cycle',
            subjectId: context.domain.hearingId ?? 1,
            personTypeCode: 'incarcerated_person',
            personId: context.domain.personId ?? 1,
            scopeTypeCode: 'global',
            scopeKey: null,
            contextHash: '0'.repeat(64),
            isCurrent: false,
            canAuthor: false,
            canCorrect: false,
          },
          page: 1,
          pageSize: 25,
          totalCount: 0,
          snapshot: 'synthetic-preview-v1',
          items: [],
        };
      else if (code === 'workflow.review.records')
        data = {
          syntheticPreview: true,
          totalCount: 2,
          rows: [
            {
              decisionRecordId: 101,
              statusCode: 'returned',
              notes: 'Synthetic example: include the missing information.',
            },
            { decisionRecordId: 102, statusCode: 'pending', notes: null },
          ],
        };
      else if (code === 'workflow.step.information')
        data = {
          syntheticPreview: true,
          occurrenceId: selection.occurrenceId,
          totalCount: 1,
          rows: [
            {
              occurrenceId: selection.occurrenceId,
              nodeId: selection.nodeId,
              occurrenceNumber: selection.occurrenceNumber,
              stepCode: selection.stepCode,
              stepLabel: selection.stepLabel,
              processName: selection.processName,
              publicReference: preview.execution.publicReference ?? null,
              definitionVersionCode: preview.execution.definitionVersionCode,
              workflowStatusCode: preview.execution.statusCode,
              stepStatusCode: selection.stepStatusCode,
              isCurrent: preview.isCurrent,
              subjectTypeCode: preview.subject.type,
              subjectId: preview.subject.id,
              queueCode: null,
              assigneeDisplayName: null,
              startedUtc: null,
              completedUtc: null,
            },
          ],
        };
      else if (code === 'workflow.step.requirements-evidence') {
        const requirements =
          checklist && checklist.occurrenceId === selection.occurrenceId
            ? checklist.requirements
            : [];
        const rows = requirements.slice(0, 100).map((row) => ({
          id: row.id,
          provider: row.provider,
          label: row.label,
          state: 'not_evaluated',
          reasonCode: 'synthetic_preview.not_evaluated',
          message:
            'Synthetic preview. This requirement has not been evaluated and no evidence is read.',
          evidence: [],
        }));
        data = {
          occurrenceId: selection.occurrenceId,
          canComplete: false,
          totalCount: requirements.length,
          evidencePosture: 'synthetic-preview',
          page: 1,
          pageSize: 100,
          rows,
        };
      } else
        data = {
          syntheticPreview: true,
          occurrenceId: selection.occurrenceId,
          page: 1,
          pageSize: 100,
          totalCount: 0,
          workflowInstanceId: context.workflow!.workflowInstanceId,
          workflowCode: preview.execution.workflowCode,
          processName: selection.processName,
          definitionVersionCode: preview.execution.definitionVersionCode,
          workflowStatusCode: preview.execution.statusCode,
          stepCode: selection.stepCode,
          stepLabel: selection.stepLabel,
          occurrenceNumber: selection.occurrenceNumber,
          stepStatusCode: selection.stepStatusCode,
          isCurrent: preview.isCurrent,
          isPartial: false,
          issueCodes: [],
          rows: [],
        };
      return {
        data,
        sources: [
          {
            provider: 'platform',
            contract: request.read.dataContract,
            recordKey: `synthetic-preview/workflow:${context.workflow!.workflowInstanceId}/visit:${selection.occurrenceId ?? 'none'}/${code}`,
            sourceVersion: 'synthetic-preview-v1',
            observedAtUtc,
            observation: 'client-receipt',
            consistency: 'independent-read',
          },
        ],
      };
    },
  };
}

function selectedPreview(
  preview: WorkflowBlockPreview,
  context: WorkspaceContext,
  requireVisit: boolean,
) {
  const workflow = context.workflow;
  if (
    !workflow ||
    workflow.workflowInstanceId !== preview.execution.workflowInstanceId ||
    workflow.definitionVersionId !== preview.definitionVersionId
  )
    throw new Error('The synthetic preview does not match this workspace.');
  const occurrenceId = workflow.stepOccurrenceId;
  if (
    requireVisit &&
    (!occurrenceId || occurrenceId !== preview.selectedOccurrenceId)
  )
    throw new Error('Select a visit in the synthetic preview.');
  const occurrence = preview.execution.occurrences.find(
    (item) => item.id === occurrenceId,
  );
  return {
    occurrenceId,
    occurrenceNumber:
      workflow.occurrenceNumber ?? occurrence?.occurrenceNumber ?? 1,
    nodeId: workflow.nodeId ?? preview.nodeId ?? 'synthetic-preview',
    stepCode: occurrence?.stepCode ?? workflow.nodeId ?? 'synthetic-preview',
    stepLabel:
      preview.stepLabel || occurrence?.stepCode || 'Synthetic preview step',
    stepStatusCode: occurrence?.statusCode ?? 'pending',
    processName: preview.processName || preview.execution.workflowCode,
  };
}
